import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, Wallet, ArrowUpRight, 
  Settings, LogOut, TrendingUp, DollarSign, Menu, X, FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Users', path: '/admin/users', icon: Users },
    { name: 'Deposits', path: '/admin/deposits', icon: Wallet },
    { name: 'Withdrawals', path: '/admin/withdrawals', icon: ArrowUpRight },
    { name: 'Profits', path: '/admin/profits', icon: DollarSign },
    { name: 'Audit Log', path: '/admin/audit', icon: FileText },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Handle Escape key to close sidebar
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/admin-login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="admin-layout">
      <div className="animated-bg" />
      
      {/* Mobile Header */}
      <header className="admin-mobile-header">
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="admin-hamburger"
          aria-label="Open menu"
          aria-expanded={isSidebarOpen}
        >
          <Menu className="w-6 h-6" />
        </button>
        <Link to="/admin/dashboard" className="admin-mobile-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-gray-900" />
          </div>
          <span className="text-lg font-bold font-['Outfit'] text-white">
            MetaBlockify
          </span>
        </Link>
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      {/* Sidebar Overlay (Mobile) */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="admin-sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        {/* Sidebar Header */}
        <div className="admin-sidebar-header">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-gray-900" />
            </div>
            <div className="admin-sidebar-brand">
              <span className="text-lg font-bold font-['Outfit'] text-white block">
                MetaBlockify
              </span>
              <span className="text-xs text-cyan-400 font-medium">ADMIN PANEL</span>
            </div>
          </Link>
          {/* Close button (mobile only) */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="admin-sidebar-close"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setIsSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="admin-sidebar-link-text">{item.name}</span>
              {isActive(item.path) && (
                <motion.div
                  layoutId="adminActiveIndicator"
                  className="admin-sidebar-active-indicator"
                />
              )}
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="admin-sidebar-footer">
          <button
            onClick={handleLogout}
            className="admin-sidebar-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="admin-sidebar-link-text">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-content">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
